import { useState } from 'react';
import {
  Stack, Group, Button, Text, Paper, Badge, Table, Tabs,
  Modal, TextInput, NumberInput, Alert, Loader, Center, Title,
  ActionIcon, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowsExchange, IconPlus, IconCheck, IconX, IconBan,
  IconAlertCircle, IconHistory,
} from '@tabler/icons-react';
import api from '@/config/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

interface ShiftSwap {
  id: number;
  requester_id: number;
  requester_name?: string;
  target_employee_id: number;
  target_employee_name?: string;
  requester_schedule_id: number;
  target_schedule_id: number;
  requester_shift_date?: string;
  target_shift_date?: string;
  requester_shift_start?: string;
  requester_shift_end?: string;
  target_shift_start?: string;
  target_shift_end?: string;
  reason: string;
  status: string;
  created_at: string;
  updated_at?: string;
}

function statusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'yellow';
    case 'target_accepted':
      return 'blue';
    case 'approved':
      return 'green';
    case 'rejected':
      return 'red';
    case 'cancelled':
      return 'gray';
    case 'completed':
      return 'teal';
    default:
      return 'gray';
  }
}

export default function MyShiftSwaps() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('outgoing');

  // Form state
  const [reason, setReason] = useState('');
  const [requesterScheduleId, setRequesterScheduleId] = useState<number | ''>('');
  const [targetEmployeeId, setTargetEmployeeId] = useState('');
  const [targetScheduleId, setTargetScheduleId] = useState<number | ''>('');

  // ---------- Query ----------

  const swapsQuery = useQuery<ShiftSwap[]>({
    queryKey: ['my-shift-swaps'],
    queryFn: async () => {
      const res = await api.get('/schedule-swaps/');
      // API may return { items: [...] } or array directly
      return Array.isArray(res.data) ? res.data : res.data.items ?? [];
    },
  });

  const allSwaps = swapsQuery.data ?? [];

  // Categorise swaps. Without knowing current user ID from the API response,
  // we rely on status + presence of "requester" / "target" fields. A more
  // robust solution would use a user context; here we expose all three views.
  const outgoing = allSwaps.filter(
    (s) => ['pending', 'target_accepted'].includes(s.status),
  );
  const incoming = allSwaps.filter(
    (s) => s.status === 'pending',
  );
  const history = allSwaps.filter(
    (s) => ['approved', 'rejected', 'cancelled', 'completed'].includes(s.status),
  );

  // ---------- Mutations ----------

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/schedule-swaps/', {
        reason,
        requester_schedule_id: requesterScheduleId,
        target_employee_id: Number(targetEmployeeId),
        target_schedule_id: targetScheduleId,
      });
      return res.data;
    },
    onSuccess: () => {
      notifications.show({ title: 'Swap Requested', message: 'Shift swap request created.', color: 'green' });
      queryClient.invalidateQueries({ queryKey: ['my-shift-swaps'] });
      resetForm();
      setCreateOpen(false);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to create swap request.', color: 'red' });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/schedule-swaps/${id}/target-accept`);
      return res.data;
    },
    onSuccess: () => {
      notifications.show({ title: 'Accepted', message: 'You accepted the swap request.', color: 'green' });
      queryClient.invalidateQueries({ queryKey: ['my-shift-swaps'] });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to accept swap.', color: 'red' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/schedule-swaps/${id}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      notifications.show({ title: 'Cancelled', message: 'Swap request cancelled.', color: 'blue' });
      queryClient.invalidateQueries({ queryKey: ['my-shift-swaps'] });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to cancel swap.', color: 'red' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/schedule-swaps/${id}/reject`);
      return res.data;
    },
    onSuccess: () => {
      notifications.show({ title: 'Rejected', message: 'Swap request rejected.', color: 'orange' });
      queryClient.invalidateQueries({ queryKey: ['my-shift-swaps'] });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to reject swap.', color: 'red' });
    },
  });

  function resetForm() {
    setReason('');
    setRequesterScheduleId('');
    setTargetEmployeeId('');
    setTargetScheduleId('');
  }

  // ---------- Render helpers ----------

  function renderSwapRow(swap: ShiftSwap, variant: 'outgoing' | 'incoming' | 'history') {
    return (
      <Table.Tr key={swap.id}>
        <Table.Td>{swap.id}</Table.Td>
        <Table.Td>{swap.target_employee_name ?? swap.target_employee_id}</Table.Td>
        <Table.Td>
          {swap.requester_shift_date
            ? `${dayjs(swap.requester_shift_date).format('MM/DD')} ${swap.requester_shift_start ? dayjs(swap.requester_shift_start).format('h:mm A') : ''}-${swap.requester_shift_end ? dayjs(swap.requester_shift_end).format('h:mm A') : ''}`
            : `Schedule #${swap.requester_schedule_id}`}
        </Table.Td>
        <Table.Td>
          {swap.target_shift_date
            ? `${dayjs(swap.target_shift_date).format('MM/DD')} ${swap.target_shift_start ? dayjs(swap.target_shift_start).format('h:mm A') : ''}-${swap.target_shift_end ? dayjs(swap.target_shift_end).format('h:mm A') : ''}`
            : `Schedule #${swap.target_schedule_id}`}
        </Table.Td>
        <Table.Td>
          <Badge color={statusColor(swap.status)} variant="light" size="sm">
            {swap.status.replace('_', ' ')}
          </Badge>
        </Table.Td>
        <Table.Td>{dayjs(swap.created_at).format('MM/DD/YYYY')}</Table.Td>
        <Table.Td>
          <Group gap={4}>
            {variant === 'outgoing' && swap.status === 'pending' && (
              <Tooltip label="Cancel">
                <ActionIcon
                  variant="light"
                  color="red"
                  size="sm"
                  onClick={() => cancelMutation.mutate(swap.id)}
                  loading={cancelMutation.isPending}
                >
                  <IconBan size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            {variant === 'incoming' && swap.status === 'pending' && (
              <>
                <Tooltip label="Accept">
                  <ActionIcon
                    variant="light"
                    color="green"
                    size="sm"
                    onClick={() => acceptMutation.mutate(swap.id)}
                    loading={acceptMutation.isPending}
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Reject">
                  <ActionIcon
                    variant="light"
                    color="red"
                    size="sm"
                    onClick={() => rejectMutation.mutate(swap.id)}
                    loading={rejectMutation.isPending}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  }

  function renderTable(data: ShiftSwap[], variant: 'outgoing' | 'incoming' | 'history') {
    if (!data.length) {
      return <Text c="dimmed" ta="center" py="lg">No swap requests found.</Text>;
    }
    return (
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>Target Employee</Table.Th>
            <Table.Th>My Shift</Table.Th>
            <Table.Th>Their Shift</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((s) => renderSwapRow(s, variant))}
        </Table.Tbody>
      </Table>
    );
  }

  // ---------- Main render ----------

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Shift Swaps</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          Request Swap
        </Button>
      </Group>

      {swapsQuery.isLoading ? (
        <Center h={300}><Loader size="lg" /></Center>
      ) : swapsQuery.isError ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load shift swaps.
        </Alert>
      ) : (
        <Paper p="md" shadow="sm" radius="md" withBorder>
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="outgoing" leftSection={<IconArrowsExchange size={16} />}>
                My Requests ({outgoing.length})
              </Tabs.Tab>
              <Tabs.Tab value="incoming" leftSection={<IconCheck size={16} />}>
                Incoming ({incoming.length})
              </Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
                History ({history.length})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="outgoing" pt="md">
              {renderTable(outgoing, 'outgoing')}
            </Tabs.Panel>
            <Tabs.Panel value="incoming" pt="md">
              {renderTable(incoming, 'incoming')}
            </Tabs.Panel>
            <Tabs.Panel value="history" pt="md">
              {renderTable(history, 'history')}
            </Tabs.Panel>
          </Tabs>
        </Paper>
      )}

      {/* ===== Create Swap Modal ===== */}
      <Modal
        opened={createOpen}
        onClose={() => { setCreateOpen(false); resetForm(); }}
        title="Request Shift Swap"
        size="md"
      >
        <Stack gap="md">
          <NumberInput
            label="My Schedule ID"
            description="The ID of the shift you want to swap away"
            value={requesterScheduleId}
            onChange={(val) => setRequesterScheduleId(val as number | '')}
            min={1}
            required
          />
          <TextInput
            label="Target Employee ID"
            description="The employee you want to swap with"
            value={targetEmployeeId}
            onChange={(e) => setTargetEmployeeId(e.currentTarget.value)}
            required
          />
          <NumberInput
            label="Target Schedule ID"
            description="The ID of the shift you want to pick up"
            value={targetScheduleId}
            onChange={(val) => setTargetScheduleId(val as number | '')}
            min={1}
            required
          />
          <TextInput
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="Why do you want to swap?"
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { setCreateOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!requesterScheduleId || !targetEmployeeId || !targetScheduleId || !reason}
            >
              Submit Request
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
